import type { SkippedMapping } from '@/api/formPopulationApi'

/**
 * Why the backend refused to write a mapping, in the operator's words.
 *
 * A skip has three possible reasons — `unknown-row`, `unknown-column` and
 * `unknown-field` — and the warning used to describe every one of them as a
 * missing row or column. A mapping onto a deleted FLAT field therefore sent the
 * operator to the Mappings tab looking for a table row that was never involved.
 *
 * Pure, and in `src/lib` rather than the dialog, because `vitest.config.ts`
 * matches `src/**\/*.test.ts` only: wording inside a `.tsx` would never run a
 * test, and this is exactly the kind of thing that drifts.
 */
export function skippedMappingsMessage(skipped: readonly SkippedMapping[]): string {
  const count = skipped.length
  if (count === 0) return ''

  const fields = skipped.filter((s) => s.reason === 'unknown-field').length
  const cells = count - fields
  const one = count === 1

  // Named for what the operator will go looking for. Mixed only ever happens
  // with two or more, since a single skip is one reason or the other.
  const missing =
    fields > 0 && cells > 0
      ? 'the form fields, rows or columns they fill are'
      : fields > 0
        ? one
          ? 'the form field it fills is'
          : 'the form fields they fill are'
        : one
          ? 'the row or column it fills is'
          : 'the rows or columns they fill are'

  const subject = one ? '1 mapping was skipped' : `${count} mappings were skipped`
  return `${subject}: ${missing} no longer on this form.`
}
